import { useEffect } from 'react';
import { BrowserRouter, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import { Splash } from './routes/Splash.js';
import { Lobby } from './routes/Lobby.js';
import { Match } from './routes/Match.js';
import { Profile } from './routes/Profile.js';
import { History } from './routes/History.js';
import { Privacy } from './routes/Privacy.js';
import { Terms } from './routes/Terms.js';
import { MatchDetail } from './routes/MatchDetail.js';
import { Rules } from './routes/Rules.js';
import { Balance } from './routes/Balance.js';
import { Toaster } from './components/Toaster.js';
import { useAuth } from './stores/auth.js';
import { bootstrapTelegram, getInitData, isInsideTelegram } from './lib/telegram.js';
import { attachGameListeners, setMatchStartHandler } from './lib/gameSocket.js';

export function App() {
  const status = useAuth((s) => s.status);
  const error = useAuth((s) => s.error);
  const token = useAuth((s) => s.token);
  const signIn = useAuth((s) => s.signInWithInitData);

  useEffect(() => {
    bootstrapTelegram();
    if (status !== 'idle') return;
    if (isInsideTelegram()) {
      void signIn(getInitData());
    }
  }, [status, signIn]);

  useEffect(() => {
    if (status !== 'ready' || !token) return;
    attachGameListeners(token);
  }, [status, token]);

  if (status === 'idle' || status === 'authing') {
    return (
      <>
        <Splash />
        <Toaster />
      </>
    );
  }
  if (status === 'error') {
    return (
      <div className="grid min-h-full place-items-center px-6 text-center">
        <div className="flex flex-col gap-3">
          <h1 className="heading-display text-3xl">Не удалось войти</h1>
          <p className="text-sm text-ivory/70">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <NavigationBridge />
      <div className="flex min-h-full flex-col">
        <main className="flex-1 pb-20">
          <Routes>
            <Route path="/" element={<Lobby />} />
            <Route path="/match/:id" element={<Match />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/history" element={<History />} />
            <Route path="/history/:id" element={<MatchDetail />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/balance" element={<Balance />} />
          </Routes>
        </main>
        <BottomNav />
      </div>
      <Toaster />
    </BrowserRouter>
  );
}

function NavigationBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    setMatchStartHandler((msg) => {
      navigate(`/match/${msg.matchId}`);
    });
    return () => setMatchStartHandler(() => {});
  }, [navigate]);
  return null;
}

function BottomNav() {
  return (
    <nav className="nav-bottom fixed inset-x-0 bottom-0 z-40 mx-auto flex w-full max-w-[480px] items-center justify-around">
      <NavLink to="/" className={({ isActive }) => `nav-item ${isActive ? 'is-active' : ''}`} end>
        <NavIconLobby />
        <span>Лобби</span>
      </NavLink>
      <NavLink to="/history" className={({ isActive }) => `nav-item ${isActive ? 'is-active' : ''}`}>
        <NavIconHistory />
        <span>История</span>
      </NavLink>
      <NavLink to="/profile" className={({ isActive }) => `nav-item ${isActive ? 'is-active' : ''}`}>
        <NavIconProfile />
        <span>Профиль</span>
      </NavLink>
    </nav>
  );
}

function NavIconLobby() {
  // Stylised stack of two cards
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <rect x="6" y="3" width="11" height="15" rx="2" transform="rotate(-8 11.5 10.5)" />
      <rect x="7.5" y="5" width="11" height="15" rx="2" transform="rotate(8 13 12.5)" />
    </svg>
  );
}

function NavIconHistory() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <polyline points="3 4 3 9 8 9" />
      <polyline points="12 7 12 12 15.5 14" />
    </svg>
  );
}

function NavIconProfile() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M5 20c.6-3.6 3.6-5.6 7-5.6s6.4 2 7 5.6" />
    </svg>
  );
}
